import { KeyStatus } from "../../core/engine/types";
import { IInputManager } from "../../core/engine/types/input-manager";
import { KeyboardConfig, DEFAULT_CONFIG } from "../components/Settings";
import { IInputSource, InputActionState } from "./input-source";

export const getDefaultConfig = (): KeyboardConfig => DEFAULT_CONFIG;

export const loadConfig = (): KeyboardConfig => {
  try {
    const saved = localStorage.getItem("keyboardConfig");
    if (saved) {
      const parsed = JSON.parse(saved);
      // Merge with defaults to ensure all keys are present
      return { ...DEFAULT_CONFIG, ...parsed };
    }
  } catch (e) {
    console.warn("Failed to load keyboard config from localStorage:", e);
  }
  return DEFAULT_CONFIG;
};

export default class KeyboardInputManager implements IInputManager, IInputSource {
  document: Document;
  keyRotate = KeyStatus.NONE;
  keyRotateTwice = KeyStatus.NONE;
  keyDown = KeyStatus.NONE;
  keyRight = KeyStatus.NONE;
  keyLeft = KeyStatus.NONE;
  keyDrop = KeyStatus.NONE;
  config: KeyboardConfig;
  private enabledFlag = true;
  private keyUpHandler?: (event: KeyboardEvent) => void;
  private keyDownHandler?: (event: KeyboardEvent) => void;
  [key: string]: any;

  constructor(document: Document, config: KeyboardConfig, enabled: boolean = true) {
    this.document = document;
    this.config = config;
    this.enabledFlag = enabled;
    this.init();
  }

  setEnabled(enabled: boolean): void {
    if (this.enabledFlag === enabled) return;

    this.enabledFlag = enabled;
    if (enabled) {
      this.init();
    } else {
      this.cleanup();
    }
  }

  isEnabled = (): boolean => {
    return this.enabledFlag;
  };

  private init() {
    if (!this.enabledFlag) return;

    this.cleanup(); // Remove old listeners if any

    this.keyUpHandler = (event: KeyboardEvent) => {
      if (!this.enabledFlag) return;
      
      let key = Object.keys(this.config).find(
        (k) => (this.config as any)[k] === event.code
      ) as keyof KeyboardConfig | undefined;
      if (key) {
        // For rotate actions, only set RELEASED if not already processed
        // This prevents infinite rotation loops
        if (key === 'keyRotate' || key === 'keyRotateTwice') {
          // Only set RELEASED if currently PRESSED or NONE (not already RELEASED)
          if ((this[key] & KeyStatus.PRESSED) || this[key] === KeyStatus.NONE) {
            this[key] = KeyStatus.RELEASED;
          }
        } else {
          // For other keys, always set RELEASED on keyup
          this[key] = KeyStatus.RELEASED;
        }
      }
    };

    this.keyDownHandler = (event: KeyboardEvent) => {
      if (!this.enabledFlag) return;
      
      let key = Object.keys(this.config).find(
        (k) => (this.config as any)[k] === event.code
      ) as keyof KeyboardConfig | undefined;
      if (key) {
        // Set PRESSED directly (don't use |= as KeyStatus is an enum, not a bitfield)
        this[key] = KeyStatus.PRESSED;
      }
    };

    this.document.addEventListener("keyup", this.keyUpHandler);
    this.document.addEventListener("keydown", this.keyDownHandler);
  }

  private cleanup() {
    if (this.keyUpHandler) {
      this.document.removeEventListener("keyup", this.keyUpHandler);
      this.keyUpHandler = undefined;
    }
    if (this.keyDownHandler) {
      this.document.removeEventListener("keydown", this.keyDownHandler);
      this.keyDownHandler = undefined;
    }
  }

  update(): void {
    // Keyboard input is event-driven, so update() is a no-op
    // The state is updated directly by event handlers
  }

  getState(): InputActionState {
    return {
      keyRotate: this.keyRotate,
      keyRotateTwice: this.keyRotateTwice,
      keyDown: this.keyDown,
      keyRight: this.keyRight,
      keyLeft: this.keyLeft,
      keyDrop: this.keyDrop,
    };
  }

  destroy(): void {
    this.cleanup();
  }
}
